import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAuthApiClient } from './AuthApiClientContext';
import { Me } from './authApiTypes';

type ReturnPromiseType<T extends Function> = T extends (
  ...args: any[]
) => Promise<infer R>
  ? R
  : any;

function createResponseHandler<
  T extends (...args: any) => Promise<Response>,
  R extends Response = ReturnPromiseType<T>
>(clientFn: T) {
  return async (...args: Parameters<T>): Promise<R> => {
    const response = await clientFn(...args);
    if (!response.ok) {
      throw response;
    }
    return response as R;
  };
}

export function useLoginMutation() {
  const apiClient = useAuthApiClient();
  const queryClient = useQueryClient();
  return useMutation(createResponseHandler(apiClient.login), {
    onSuccess: () => {
      queryClient.resetQueries();
    },
  });
}

export function useLoginAsMutiation() {
  const apiClient = useAuthApiClient();
  const queryClient = useQueryClient();
  return useMutation(createResponseHandler(apiClient.loginAs), {
    onSuccess: () => {
      queryClient.resetQueries();
    },
  });
}

export function useLogoutMutation() {
  const apiClient = useAuthApiClient();
  const queryClient = useQueryClient();
  return useMutation(createResponseHandler(apiClient.logout), {
    onSuccess: () => {
      queryClient.resetQueries();
    },
  });
}

export function useMeQuery() {
  const apiClient = useAuthApiClient();
  return useQuery(['auth', 'me'], async () => {
    const response = await apiClient.getMe();
    if (response.ok) {
      return { response, me: (await response.json()) as Me };
    } else if (response.status === 401) {
      return { response, me: null };
    }
    throw response;
  });
}

export function useRefreshTokenMutation() {
  const apiClient = useAuthApiClient();
  const queryClient = useQueryClient();
  return useMutation(createResponseHandler(apiClient.refreshToken), {
    onError: () => {
      queryClient.invalidateQueries('auth');
    },
  });
}
